import { Link } from "react-router-dom";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../clients/utils/AuthContext";
import { Menu, X, ChevronDown, LogIn, UserPlus, User, CalendarCheck, LogOut } from "lucide-react";
import axios from "axios";

function LandingHeader() {
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false); // ✅ scroll réel

  const { user, dispatch } = useContext(AuthContext);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post("https://api.komorotel.com/api/auth/logout", {}, { withCredentials: true });
      dispatch({ type: "LOGOUT" });
      setDropdown(false);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <header
      className={`
        fixed top-0 left-0 w-full z-50 transition-all duration-300
        ${scrolled
          ? "bg-white shadow-md border-b border-gray-100"
          : "bg-transparent"}
      `}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 py-4">

        {/* LOGO */}
        <Link
          to="/"
          className={`text-xl font-semibold tracking-tight transition-colors duration-300 ${
            scrolled ? "text-gray-900" : "text-white"
          }`}
        >
          Komorotel
        </Link>

        {/* DESKTOP */}
        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setDropdown(!dropdown)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl transition ${
                  scrolled ? "hover:bg-gray-100" : "hover:bg-white/10"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-[#3A9D23] text-white flex items-center justify-center text-sm font-medium">
                  {user.name?.[0]?.toUpperCase() || "U"}
                </div>
                <span className={`transition-colors duration-300 ${scrolled ? "text-gray-900" : "text-white"}`}>
                  {user.name}
                </span>
                <ChevronDown size={16} className={scrolled ? "text-gray-600" : "text-white/80"} />
              </button>

              {dropdown && (
                <div className="absolute right-0 mt-3 w-64 rounded-2xl shadow-xl border py-2 z-50 backdrop-blur-md bg-white/90">
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#3A9D23] text-white flex items-center justify-center font-semibold">
                      {(user?.name || user?.email || "?")[0]?.toUpperCase()}
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || "Utilisateur"}</p>
                      {user?.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                    </div>
                  </div>
                  <div className="h-px bg-gray-100 my-2" />
                  <Link to="/profile" onClick={() => setDropdown(false)} className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 transition">
                    <User size={16} /> Mon profil
                  </Link>
                  <Link to="/my-reservations" onClick={() => setDropdown(false)} className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 transition">
                    <CalendarCheck size={16} /> Mes réservations
                  </Link>
                  <div className="h-px bg-gray-100 my-2" />
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition">
                    <LogOut size={16} /> Déconnexion
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className={`text-sm transition-colors duration-300 ${scrolled ? "text-gray-700" : "text-white/90"}`}>
                Se connecter
              </Link>
              <Link to="/signup" className="bg-white text-[#3A9D23] px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition">
                S'inscrire
              </Link>
            </>
          )}
        </div>

        {/* MOBILE BUTTON */}
        <button
          className={`md:hidden transition-colors duration-300 ${scrolled ? "text-gray-900" : "text-white"}`}
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* MOBILE MENU */}
      {/* MOBILE MENU */}
        {open && (
          <div className="fixed inset-0 z-[999] bg-white flex flex-col isolate">
            
            {/* HEADER — seulement si user connecté, sinon juste le X */}
            <div className="flex items-center justify-between px-6 py-5 border-b">
              {user ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3A9D23] to-[#2f7d1c] text-white flex items-center justify-center font-semibold shadow-sm">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{user.name}</p>
                    {user.email && <p className="text-xs text-gray-500">{user.email}</p>}
                  </div>
                </div>
              ) : (
                <span className="font-semibold text-gray-900">Menu</span>
              )}
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition">
                <X size={20} />
              </button>
            </div>

            {/* CONTENT */}
            <div className="flex-1 px-6 py-6 flex flex-col gap-6">
              {user ? (
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Compte</p>
                  <Link to="/profile" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 transition font-medium">
                    <User size={18} className="text-gray-400" /> Mon profil
                  </Link>
                  <Link to="/my-reservations" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 transition font-medium">
                    <CalendarCheck size={18} className="text-gray-400" /> Mes réservations
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col gap-3 mt-4">
                  <Link to="/login" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-[#3A9D23] text-white font-medium hover:bg-[#33891E] transition">
                    <LogIn size={18} /> Se connecter
                  </Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl border border-gray-200 font-medium hover:bg-gray-50 transition">
                    <UserPlus size={18} /> S'inscrire
                  </Link>
                </div>
              )}
            </div>

            {user && (
              <div className="p-6 border-t">
                <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition font-medium">
                  <LogOut size={18} /> Déconnexion
                </button>
              </div>
            )}
          </div>
        )}
    </header>
  );
}

export default LandingHeader;